import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Loader2, Database, Save, RotateCcw, Hash, Edit, FileText, Mail, Wrench } from 'lucide-react'
import { useEmailDrafterStore } from '@/stores/email-drafter'
import { useSystemPrompts } from '@/hooks/use-system-prompts'
import { useToast } from '@/hooks/use-toast'

interface PromptsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PromptsModal({ open, onOpenChange }: PromptsModalProps) {
  const { session, updateSession } = useEmailDrafterStore()
  const { prompts, loading, getPrompt, updatePrompt, refreshPrompts } = useSystemPrompts()
  const { toast } = useToast()
  const [localPrompts, setLocalPrompts] = useState(session.systemprompts)
  const [saving, setSaving] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const { supabase } = await import('@/lib/supabase')
      const { data: { user } } = await supabase.auth.getUser()
      setIsAdmin(user?.email === 'admin@mission-solar.at' || user?.email?.includes('admin') || false)
    }
    checkAdmin()
  }, [])

  // Load prompts from database when modal opens
  useEffect(() => {
    if (open && !loading) {
      const loadedPrompts = {
        categorization: getPrompt('categorization') || getPrompt('question_extractor'),
        emaildrafter: getPrompt('emaildrafter') || getPrompt('email_styler_bernhard'),
        missionsolar: getPrompt('missionsolar') || getPrompt('mission_solar_refiner'),
        neoom: getPrompt('neoom') || getPrompt('neoom_refiner'),
        selfsolve: getPrompt('selfsolve') || getPrompt('self_solve_refiner'),
      }
      setLocalPrompts(loadedPrompts)
      updateSession({ systemprompts: loadedPrompts })
    }
  }, [open, loading, prompts])

  const handlePromptChange = (field: string, value: string) => {
    setLocalPrompts(prev => ({ ...prev, [field]: value }))
  }

  const saveToDatabase = async () => {
    if (!isAdmin) {
      toast({
        title: 'Keine Berechtigung',
        description: 'Nur Administratoren können Prompts in der Datenbank speichern',
        variant: 'destructive'
      })
      return
    }

    setSaving(true)
    try {
      // Map fields to database prompt names
      const mappings = [
        { field: 'categorization', dbName: 'question_extractor' },
        { field: 'emaildrafter', dbName: 'email_styler_bernhard' },
        { field: 'missionsolar', dbName: 'mission_solar_refiner' },
        { field: 'neoom', dbName: 'neoom_refiner' },
        { field: 'selfsolve', dbName: 'self_solve_refiner' }
      ]

      for (const { field, dbName } of mappings) {
        const value = localPrompts[field as keyof typeof localPrompts]
        if (value) {
          await updatePrompt(dbName, value)
        }
      }

      toast({
        title: 'Prompts gespeichert',
        description: 'Alle Prompts wurden in der Datenbank aktualisiert',
      })
    } catch (error) {
      console.error('Error saving prompts:', error)
    } finally {
      setSaving(false)
    }
  }

  const saveToSession = () => {
    updateSession({ systemprompts: localPrompts })
    toast({
      title: 'Sitzung aktualisiert',
      description: 'Prompts wurden für diese Sitzung übernommen',
    })
    onOpenChange(false)
  }

  const resetPrompts = async () => {
    setLocalPrompts({
      categorization: '',
      emaildrafter: '',
      missionsolar: '',
      neoom: '',
      selfsolve: '',
    })
    await refreshPrompts()
    toast({
      title: 'Zurückgesetzt',
      description: 'Prompts wurden auf Standardwerte zurückgesetzt',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            System Prompts konfigurieren
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            Passen Sie die System-Prompts für die Email-Generierung an.
            {isAdmin ? (
              <Badge variant="default">Admin-Modus</Badge>
            ) : (
              <Badge variant="secondary">Nur Sitzung</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="categorization" className="flex-1">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="categorization" className="text-xs">
              <Hash className="h-3 w-3 mr-1" />
              Extractor
            </TabsTrigger>
            <TabsTrigger value="emaildrafter" className="text-xs">
              <FileText className="h-3 w-3 mr-1" />
              Styler
            </TabsTrigger>
            <TabsTrigger value="missionsolar" className="text-xs">
              <Edit className="h-3 w-3 mr-1" />
              Mission
            </TabsTrigger>
            <TabsTrigger value="neoom" className="text-xs">
              <Wrench className="h-3 w-3 mr-1" />
              Neoom
            </TabsTrigger>
            <TabsTrigger value="selfsolve" className="text-xs">
              <Mail className="h-3 w-3 mr-1" />
              Self-Solve
            </TabsTrigger>
          </TabsList>

          <TabsContent value="categorization" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span className="font-semibold">Question Extractor</span>
                <Badge variant="outline" className="text-xs">question_extractor</Badge>
              </Label>
              <p className="text-sm text-muted-foreground">
                Extrahiert Kundenanfragen und technische Details aus Emails (JSON Output)
              </p>
              <Textarea
                value={localPrompts.categorization}
                onChange={(e) => handlePromptChange('categorization', e.target.value)}
                placeholder={loading ? "Lade Prompt..." : "Prompt für Email-Kategorisierung..."}
                rows={20}
                className="resize-vertical font-mono text-xs"
                disabled={loading}
              />
            </div>
          </TabsContent>

          <TabsContent value="emaildrafter" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Email Styler - Bernhard</span>
                  <Badge variant="outline" className="text-xs">
                    {(() => {
                      const bernhardPrompt = prompts.find(p => p.name === 'email_styler_bernhard' && p.is_latest)
                      return bernhardPrompt ? `v${bernhardPrompt.version}` : 'v?.?.?'
                    })()}
                  </Badge>
                </div>
                <Badge variant="outline" className="text-xs">email_styler_bernhard</Badge>
              </Label>
              <p className="text-sm text-muted-foreground">
                Persönlicher Email-Stil mit österreichischem Touch
              </p>
              <Textarea
                value={localPrompts.emaildrafter}
                onChange={(e) => handlePromptChange('emaildrafter', e.target.value)}
                placeholder={loading ? "Lade Prompt..." : "Haupt-Prompt für Email-Erstellung..."}
                rows={20}
                className="resize-vertical font-mono text-xs"
                disabled={loading}
              />
            </div>
          </TabsContent>

          <TabsContent value="missionsolar" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span className="font-semibold">Mission Solar Refiner</span>
                <Badge variant="outline" className="text-xs">mission_solar_refiner</Badge>
              </Label>
              <p className="text-sm text-muted-foreground">
                Verfeinert Email-Antworten für Mission Solar Standard-Fälle (HTML Output)
              </p>
              <Textarea
                value={localPrompts.missionsolar}
                onChange={(e) => handlePromptChange('missionsolar', e.target.value)}
                placeholder={loading ? "Lade Prompt..." : "Prompt für Mission Solar spezifische Antworten..."}
                rows={20}
                className="resize-vertical font-mono text-xs"
                disabled={loading}
              />
            </div>
          </TabsContent>

          <TabsContent value="neoom" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span className="font-semibold">Neoom Technical Refiner</span>
                <Badge variant="outline" className="text-xs">neoom_refiner</Badge>
              </Label>
              <p className="text-sm text-muted-foreground">
                Verfeinert Email-Antworten für technische Neoom-Eskalationen (HTML Output)
              </p>
              <Textarea
                value={localPrompts.neoom}
                onChange={(e) => handlePromptChange('neoom', e.target.value)}
                placeholder={loading ? "Lade Prompt..." : "Prompt für Neoom spezifische Antworten..."}
                rows={20}
                className="resize-vertical font-mono text-xs"
                disabled={loading}
              />
            </div>
          </TabsContent>

          <TabsContent value="selfsolve" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span className="font-semibold">Self-Solve Refiner</span>
                <Badge variant="outline" className="text-xs">self_solve_refiner</Badge>
              </Label>
              <p className="text-sm text-muted-foreground">
                Verfeinert Email-Antworten mit Schritt-für-Schritt Anleitungen (HTML Output)
              </p>
              <Textarea
                value={localPrompts.selfsolve}
                onChange={(e) => handlePromptChange('selfsolve', e.target.value)}
                placeholder={loading ? "Lade Prompt..." : "Prompt für Self-Solve Antworten..."}
                rows={20}
                className="resize-vertical font-mono text-xs"
                disabled={loading}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={resetPrompts}
            disabled={loading}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Auf Standard zurücksetzen
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            {isAdmin && (
              <Button
                variant="secondary"
                onClick={saveToDatabase}
                disabled={loading || saving}
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichere...</>
                ) : (
                  <><Database className="h-4 w-4 mr-2" /> In Datenbank speichern</>
                )}
              </Button>
            )}
            <Button
              onClick={saveToSession}
              disabled={loading}
            >
              <Save className="h-4 w-4 mr-2" />
              Für Sitzung übernehmen
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}